import { renderTemplateContent } from "../contentRenderer";
import { mergeTemplateDesign, templateDesignToCssVars } from "../design";
import { DefaultTemplateLink } from "../templateLink";
import { WebBuilderPageRegion } from "../WebBuilderPageRegion";
import { WebGlobalBuilderRegion } from "../WebGlobalBuilderRegion";
import { webSystemTemplateDesignDefaults } from "../webSystemTemplateDesign";
import { CraftsmanMobileMenu } from "./CraftsmanMobileMenu";
import type {
    ResolvedTemplateDesign,
    SiteTemplateDefinition,
    TemplateLinkComponent,
    TemplateRenderProps,
} from "../types";

function CraftsmanStyles() {
    return (
        <style>{`
            [data-web-template="craftsman-modern"] {
                background: var(--site-background);
                color: var(--site-text);
                isolation: isolate;
                scroll-behavior: smooth;
                scroll-padding-top: 108px;
            }

            [data-web-template="craftsman-modern"] ::selection {
                background: var(--site-accent);
                color: var(--site-surface);
            }

            [data-web-template="craftsman-modern"] :focus-visible {
                outline: 3px solid var(--site-accent);
                outline-offset: 4px;
            }

            [data-web-template="craftsman-modern"] *,
            [data-web-template="craftsman-modern"] *::before,
            [data-web-template="craftsman-modern"] *::after {
                box-sizing: border-box;
            }

            [data-web-template="craftsman-modern"] .site-builder-section {
                position: relative;
                padding: clamp(4.5rem, 8vw, 8rem) 1.25rem;
            }

            [data-web-template="craftsman-modern"] .site-builder-grid {
                display: grid;
                gap: clamp(1.25rem, 2.6vw, 2.5rem);
                margin-inline: auto;
                width: min(100%, var(--site-container));
            }

            [data-web-template="craftsman-modern"] .site-builder-grid:not(.site-builder-grid-placed) {
                grid-template-columns: repeat(12, minmax(0, 1fr));
            }

            [data-web-template="craftsman-modern"] .site-builder-grid:not(.site-builder-grid-placed) > .site-builder-grid-item {
                grid-column: 1 / -1;
            }

            [data-web-template="craftsman-modern"] .site-builder-column {
                align-content: start;
                display: grid;
                gap: 1rem;
                min-width: 0;
            }

            [data-web-template="craftsman-modern"] .site-builder-column-inline {
                align-items: center;
                display: flex;
                flex-wrap: wrap;
            }

            [data-web-template="craftsman-modern"] .site-builder-text-h1,
            [data-web-template="craftsman-modern"] .site-builder-text-h2,
            [data-web-template="craftsman-modern"] .site-builder-text-h3 {
                text-wrap: balance;
            }

            [data-web-template="craftsman-modern"] .site-builder-button {
                align-items: center;
                letter-spacing: .09em;
                text-transform: uppercase;
                transition: background-color 180ms ease, border-color 180ms ease, color 180ms ease, transform 180ms ease;
            }

            [data-web-template="craftsman-modern"] .site-builder-button:hover {
                transform: translateX(4px);
            }

            [data-web-template="craftsman-modern"] .site-builder-card {
                transition: border-color 220ms ease, box-shadow 220ms ease, transform 220ms ease;
            }

            [data-web-template="craftsman-modern"] .site-builder-card:hover {
                border-color: color-mix(in srgb, var(--site-accent) 58%, var(--site-border)) !important;
                box-shadow: 0 18px 48px color-mix(in srgb, var(--site-primary) 13%, transparent);
                transform: translateY(-4px);
            }

            [data-web-template="craftsman-modern"] .site-builder-navigation {
                align-items: center;
                display: flex;
                flex-wrap: wrap;
                gap: clamp(1rem, 2vw, 2rem);
                justify-content: center;
            }

            [data-web-template="craftsman-modern"] .site-builder-navigation-item {
                display: inline-flex;
            }

            [data-web-template="craftsman-modern"] .site-builder-navigation-link {
                color: var(--site-muted);
                font-size: .84rem;
                font-weight: 750;
                letter-spacing: .02em;
                position: relative;
                transition: color 160ms ease;
            }

            [data-web-template="craftsman-modern"] .site-builder-navigation-link::after {
                background: var(--site-accent);
                bottom: -.45rem;
                content: "";
                height: 2px;
                left: 0;
                position: absolute;
                transform: scaleX(0);
                transform-origin: left;
                transition: transform 160ms ease;
                width: 100%;
            }

            [data-web-template="craftsman-modern"] .site-builder-navigation-link:hover {
                color: var(--site-text);
            }

            [data-web-template="craftsman-modern"] .site-builder-navigation-link:hover::after {
                transform: scaleX(1);
            }

            [data-web-template="craftsman-modern"] [data-web-region="header"] {
                left: 0;
                padding-inline: 1.25rem;
                position: sticky;
                right: 0;
                top: 34px;
                transition: background-color 220ms ease, box-shadow 220ms ease;
                z-index: 60;
            }

            [data-web-template="craftsman-modern"] [data-web-region="serviceStrip"] {
                left: 0;
                padding-inline: 1.25rem;
                position: sticky;
                right: 0;
                top: 0;
                z-index: 62;
            }

            [data-web-template="craftsman-modern"] [data-web-region="header"][data-craftsman-header-scrolled] {
                box-shadow: 0 12px 36px color-mix(in srgb, var(--site-primary) 17%, transparent);
            }

            [data-web-template="craftsman-modern"] [data-web-region="serviceStrip"] > .site-builder-grid,
            [data-web-template="craftsman-modern"] [data-web-region="header"] > .site-builder-grid {
                align-items: center;
            }

            [data-web-template="craftsman-modern"] [data-document-role="brand"] {
                position: relative;
            }

            [data-web-template="craftsman-modern"] [data-document-role="brand"]::after {
                background: var(--site-accent);
                clip-path: polygon(50% 0, 100% 0, 50% 100%, 0 100%);
                content: "";
                height: 74px;
                opacity: .16;
                pointer-events: none;
                position: absolute;
                right: -28px;
                top: 0;
                width: 44px;
            }

            [data-web-template="craftsman-modern"] [data-document-role="headerActions"] {
                flex-wrap: nowrap;
                justify-content: flex-end;
            }

            [data-web-template="craftsman-modern"] [data-document-role="heroVisual"] .site-builder-image,
            [data-web-template="craftsman-modern"] [data-document-role="projectVisual"] .site-builder-image {
                height: 100% !important;
                min-height: 100%;
                object-fit: cover;
                transition: filter 500ms cubic-bezier(.2,.7,.2,1), transform 700ms cubic-bezier(.2,.7,.2,1);
                width: 100% !important;
            }

            [data-web-template="craftsman-modern"] [data-document-role="heroVisual"],
            [data-web-template="craftsman-modern"] [data-document-role="projectVisual"] {
                isolation: isolate;
                overflow: hidden;
                position: relative;
            }

            [data-web-template="craftsman-modern"] [data-document-role="heroVisual"]::after {
                background: var(--site-accent);
                bottom: -1px;
                clip-path: polygon(0 70%, 100% 0, 100% 100%, 0 100%);
                content: "";
                height: clamp(110px, 15cqw, 230px);
                left: -1px;
                mix-blend-mode: multiply;
                opacity: .82;
                pointer-events: none;
                position: absolute;
                width: clamp(150px, 20cqw, 310px);
                z-index: 2;
            }

            [data-web-template="craftsman-modern"] [data-document-role="projectVisual"] {
                clip-path: polygon(0 0, 100% 0, 100% calc(100% - 42px), calc(100% - 42px) 100%, 0 100%);
            }

            [data-web-template="craftsman-modern"] [data-document-role="projectVisual"] .site-builder-image {
                filter: saturate(.72) contrast(1.04);
            }

            [data-web-template="craftsman-modern"] [data-document-role="projectVisual"]:hover .site-builder-image {
                filter: saturate(1) contrast(1);
                transform: scale(1.025);
            }

            [data-web-template="craftsman-modern"] [data-web-region="hero"] {
                overflow: hidden;
                padding-inline: 0;
            }

            [data-web-template="craftsman-modern"] [data-web-region="hero"] > .site-builder-grid {
                gap: 0 !important;
                grid-template-columns: minmax(0, 1fr) minmax(480px, 1fr) !important;
                max-width: none;
                width: 100%;
            }

            [data-web-template="craftsman-modern"] [data-web-region="hero"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="heroCopy"]) {
                grid-column: 1 !important;
                grid-row: 1 !important;
            }

            [data-web-template="craftsman-modern"] [data-web-region="hero"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="heroVisual"]) {
                grid-column: 2 !important;
                grid-row: 1 !important;
            }

            [data-web-template="craftsman-modern"] [data-document-role="heroCopy"] {
                padding-left: max(1.25rem, calc((100cqw - var(--site-container)) / 2));
                padding-right: clamp(1.25rem, 4vw, 4rem);
            }

            [data-web-template="craftsman-modern"] [data-document-role="heroActions"] {
                align-items: stretch;
            }

            [data-web-template="craftsman-modern"] [data-document-role="heroCopy"]:has(> [data-document-role="heroHeading"]) {
                column-gap: 12px;
                display: grid;
                grid-template-columns: repeat(12, minmax(0, 1fr));
                row-gap: 24px;
                width: 100%;
            }

            [data-web-template="craftsman-modern"] [data-document-role="heroEyebrow"],
            [data-web-template="craftsman-modern"] [data-document-role="heroHeading"],
            [data-web-template="craftsman-modern"] [data-document-role="heroDescription"],
            [data-web-template="craftsman-modern"] [data-document-role="heroProof"] {
                grid-column: 1 / -1;
            }

            [data-web-template="craftsman-modern"] [data-document-role="heroPrimaryAction"] {
                grid-column: 1 / span 6;
            }

            [data-web-template="craftsman-modern"] [data-document-role="heroSecondaryAction"] {
                grid-column: 7 / span 6;
            }

            [data-web-template="craftsman-modern"] [data-web-region="proof"] {
                padding-inline: 1.25rem;
            }

            [data-web-template="craftsman-modern"] [data-web-region="proof"] > .site-builder-grid {
                gap: 0 !important;
            }

            [data-web-template="craftsman-modern"] [data-web-region="proof"] > .site-builder-grid > .site-builder-grid-item {
                border-right: 1px solid color-mix(in srgb, var(--site-surface) 18%, transparent);
                padding-inline: clamp(1rem, 2vw, 2rem);
            }

            [data-web-template="craftsman-modern"] [data-web-region="proof"] > .site-builder-grid > .site-builder-grid-item:first-child {
                padding-left: 0;
            }

            [data-web-template="craftsman-modern"] [data-web-region="proof"] > .site-builder-grid > .site-builder-grid-item:last-child {
                border-right: 0;
            }

            [data-web-template="craftsman-modern"] [data-web-region="services"] .site-builder-card,
            [data-web-template="craftsman-modern"] [data-web-region="servicesOverview"] .site-builder-card {
                position: relative;
            }

            [data-web-template="craftsman-modern"] [data-web-region="process"] .site-builder-card {
                box-shadow: none;
            }

            [data-web-template="craftsman-modern"] [data-web-region="process"] .site-builder-card:hover {
                box-shadow: none;
            }

            [data-web-template="craftsman-modern"] [data-site-builder-block="accordion"] {
                border-top: 1px solid var(--site-border);
            }

            [data-web-template="craftsman-modern"] [data-site-builder-block="accordionItem"] {
                border-bottom: 1px solid var(--site-border);
            }

            [data-web-template="craftsman-modern"] [data-web-region$="Cta"] .site-builder-button {
                width: 100% !important;
            }

            [data-web-template="craftsman-modern"] [data-web-region="footer"] a {
                transition: color 180ms ease;
            }

            [data-web-template="craftsman-modern"] [data-web-region="footer"] a:hover {
                color: color-mix(in srgb, var(--site-accent) 30%, var(--site-surface)) !important;
            }

            [data-web-template="craftsman-modern"]:not([data-web-template-editor="true"])[data-craftsman-runtime-ready] [data-craftsman-reveal]:not([data-craftsman-revealed]) {
                opacity: 0;
                transform: translateY(26px);
            }

            [data-web-template="craftsman-modern"]:not([data-web-template-editor="true"])[data-craftsman-runtime-ready] [data-craftsman-reveal] {
                opacity: 1;
                transform: translateY(0);
                transition: opacity 700ms cubic-bezier(.2,.7,.2,1), transform 700ms cubic-bezier(.2,.7,.2,1);
                will-change: opacity, transform;
            }

            [data-web-template="craftsman-modern"] .craftsman-mobile-menu-toggle {
                display: none;
            }

            @container (max-width: 900px) {
                [data-web-template="craftsman-modern"] .site-builder-grid[data-site-builder-stack-on-mobile="true"] {
                    grid-auto-rows: auto !important;
                    grid-template-columns: minmax(0, 1fr) !important;
                    grid-template-rows: none !important;
                }

                [data-web-template="craftsman-modern"] .site-builder-grid[data-site-builder-stack-on-mobile="true"] > .site-builder-grid-item {
                    grid-column: 1 !important;
                    grid-row: auto !important;
                }

                [data-web-template="craftsman-modern"] [data-web-region="proof"] > .site-builder-grid {
                    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
                }

                [data-web-template="craftsman-modern"] [data-web-region="proof"] > .site-builder-grid > .site-builder-grid-item {
                    border-bottom: 1px solid color-mix(in srgb, var(--site-surface) 18%, transparent);
                    grid-column: auto !important;
                    grid-row: auto !important;
                    min-height: 128px;
                    padding: 1.25rem !important;
                }

                [data-web-template="craftsman-modern"] [data-web-region="serviceStrip"] > .site-builder-grid {
                    grid-template-columns: minmax(0, 1fr) auto !important;
                }

                [data-web-template="craftsman-modern"] [data-web-region="serviceStrip"] > .site-builder-grid > .site-builder-grid-item {
                    grid-column: auto !important;
                    grid-row: 1 !important;
                }

                [data-web-template="craftsman-modern"] [data-web-region="header"] {
                    min-height: 74px;
                    padding-inline: 1rem;
                }

                [data-web-template="craftsman-modern"] [data-web-region="hero"] > .site-builder-grid {
                    grid-template-columns: minmax(0, 1fr) !important;
                }

                [data-web-template="craftsman-modern"] [data-web-region="hero"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="heroCopy"]),
                [data-web-template="craftsman-modern"] [data-web-region="hero"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="heroVisual"]) {
                    grid-column: 1 !important;
                    grid-row: auto !important;
                }

                [data-web-template="craftsman-modern"] [data-document-role="heroCopy"] {
                    padding: clamp(3.5rem, 9vw, 5rem) 1.25rem !important;
                }

                [data-web-template="craftsman-modern"] [data-document-role="heroVisual"] {
                    min-height: 560px !important;
                }

                [data-web-template="craftsman-modern"] [data-document-role="heroCopy"]:has(> [data-document-role="heroHeading"]) {
                    grid-template-columns: minmax(0, 1fr);
                }

                [data-web-template="craftsman-modern"] [data-document-role="heroEyebrow"],
                [data-web-template="craftsman-modern"] [data-document-role="heroHeading"],
                [data-web-template="craftsman-modern"] [data-document-role="heroDescription"],
                [data-web-template="craftsman-modern"] [data-document-role="heroPrimaryAction"],
                [data-web-template="craftsman-modern"] [data-document-role="heroSecondaryAction"],
                [data-web-template="craftsman-modern"] [data-document-role="heroProof"] {
                    grid-column: 1;
                }

                [data-web-template="craftsman-modern"] [data-web-region="header"] > .site-builder-grid {
                    grid-template-columns: minmax(0, 1fr) auto !important;
                }

                [data-web-template="craftsman-modern"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="brand"]) {
                    display: block;
                    grid-column: 1 !important;
                    grid-row: 1 !important;
                }

                [data-web-template="craftsman-modern"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerNavigation"]),
                [data-web-template="craftsman-modern"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerActions"]) {
                    display: none;
                }

                [data-web-template="craftsman-modern"] [data-web-region="header"] .craftsman-mobile-menu-toggle {
                    align-items: center;
                    background: var(--site-surface);
                    border: 1px solid var(--site-border);
                    color: var(--site-primary);
                    cursor: pointer;
                    display: grid;
                    gap: 5px;
                    grid-column: 2;
                    grid-row: 1;
                    height: 44px;
                    justify-self: end;
                    padding: 10px;
                    place-content: center;
                    position: absolute;
                    right: 1rem;
                    top: 14px;
                    width: 44px;
                    z-index: 4;
                }

                [data-web-template="craftsman-modern"] .craftsman-mobile-menu-toggle > span {
                    background: currentColor;
                    display: block;
                    height: 2px;
                    transition: opacity 160ms ease, transform 160ms ease;
                    width: 22px;
                }

                [data-web-template="craftsman-modern"] [data-web-region="header"][data-craftsman-mobile-menu-open] {
                    background: var(--site-surface) !important;
                    bottom: 0;
                    left: 0;
                    overflow-y: auto;
                    position: fixed !important;
                    right: 0;
                    top: 0;
                    z-index: 80;
                }

                [data-web-template="craftsman-modern"] [data-web-region="header"][data-craftsman-mobile-menu-open] > .site-builder-grid {
                    align-content: start;
                    display: grid;
                    min-height: 100%;
                    padding-bottom: 2rem;
                    padding-top: 0;
                }

                [data-web-template="craftsman-modern"] [data-web-region="header"][data-craftsman-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerNavigation"]),
                [data-web-template="craftsman-modern"] [data-web-region="header"][data-craftsman-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerActions"]) {
                    display: block;
                    grid-column: 1 / -1 !important;
                }

                [data-web-template="craftsman-modern"] [data-web-region="header"][data-craftsman-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerNavigation"]) {
                    grid-row: 2 !important;
                    margin-top: clamp(3rem, 10vh, 6rem);
                }

                [data-web-template="craftsman-modern"] [data-web-region="header"][data-craftsman-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerActions"]) {
                    grid-row: 3 !important;
                }

                [data-web-template="craftsman-modern"] [data-web-region="header"][data-craftsman-mobile-menu-open] .site-builder-navigation {
                    align-items: stretch;
                    flex-direction: column;
                    gap: 0;
                }

                [data-web-template="craftsman-modern"] [data-web-region="header"][data-craftsman-mobile-menu-open] [data-document-role="headerNavigation"] {
                    align-items: stretch;
                    flex-direction: column;
                    gap: 0 !important;
                }

                [data-web-template="craftsman-modern"] [data-web-region="header"][data-craftsman-mobile-menu-open] .site-builder-navigation-item {
                    border-bottom: 1px solid var(--site-border);
                    padding-block: 1rem;
                }

                [data-web-template="craftsman-modern"] [data-web-region="header"][data-craftsman-mobile-menu-open] [data-document-role="headerNavigation"] > [data-site-builder-block="text"] {
                    border-bottom: 1px solid var(--site-border);
                    padding-block: 1rem;
                    width: 100%;
                }

                [data-web-template="craftsman-modern"] [data-web-region="header"][data-craftsman-mobile-menu-open] .site-builder-navigation-link {
                    color: var(--site-text);
                    font-family: var(--site-heading-font);
                    font-size: clamp(1.8rem, 8vw, 2.75rem);
                    font-weight: 700;
                    line-height: 1;
                    width: 100%;
                }

                [data-web-template="craftsman-modern"] [data-web-region="header"][data-craftsman-mobile-menu-open] [data-document-role="headerNavigation"] .site-builder-text {
                    color: var(--site-text) !important;
                    display: block;
                    font-family: var(--site-heading-font) !important;
                    font-size: clamp(1.8rem, 8vw, 2.75rem) !important;
                    font-weight: 700 !important;
                    line-height: 1 !important;
                    width: 100% !important;
                }

                [data-web-template="craftsman-modern"] [data-web-region="header"][data-craftsman-mobile-menu-open] [data-document-role="headerActions"] {
                    align-items: stretch;
                    flex-direction: column;
                    margin-top: 1rem;
                }

                [data-web-template="craftsman-modern"] [data-web-region="header"][data-craftsman-mobile-menu-open] [data-document-role="headerActions"] > * {
                    width: 100%;
                }

                [data-web-template="craftsman-modern"] [data-web-region="header"][data-craftsman-mobile-menu-open] [data-document-role="headerActions"] .site-builder-button {
                    width: 100% !important;
                }

                [data-web-template="craftsman-modern"] [data-web-region="header"][data-craftsman-mobile-menu-open] .craftsman-mobile-menu-toggle > span:first-child {
                    transform: translateY(7px) rotate(45deg);
                }

                [data-web-template="craftsman-modern"] [data-web-region="header"][data-craftsman-mobile-menu-open] .craftsman-mobile-menu-toggle > span:nth-child(2) {
                    opacity: 0;
                }

                [data-web-template="craftsman-modern"] [data-web-region="header"][data-craftsman-mobile-menu-open] .craftsman-mobile-menu-toggle > span:last-child {
                    transform: translateY(-7px) rotate(-45deg);
                }
            }

            @container (max-width: 640px) {
                [data-web-template="craftsman-modern"] .site-builder-section {
                    padding: 4rem 1rem;
                }

                [data-web-template="craftsman-modern"] [data-web-region="serviceStrip"] {
                    padding-inline: 1rem;
                }

                [data-web-template="craftsman-modern"] [data-web-region="serviceStrip"] > .site-builder-grid > .site-builder-grid-item:last-child {
                    display: none;
                }

                [data-web-template="craftsman-modern"] [data-web-region="serviceStrip"] > .site-builder-grid {
                    grid-template-columns: minmax(0, 1fr) !important;
                }

                [data-web-template="craftsman-modern"] .site-builder-column-inline[data-site-builder-stack-on-mobile="true"],
                [data-web-template="craftsman-modern"] [data-document-role="heroActions"] {
                    align-items: stretch;
                    flex-direction: column;
                }

                [data-web-template="craftsman-modern"] [data-document-role="heroActions"] > * {
                    width: 100%;
                }

                [data-web-template="craftsman-modern"] [data-document-role="heroCopy"] > * {
                    grid-column: 1 !important;
                }

                [data-web-template="craftsman-modern"] [data-document-role="heroPrimaryAction"],
                [data-web-template="craftsman-modern"] [data-document-role="heroSecondaryAction"] {
                    width: 100% !important;
                }

                [data-web-template="craftsman-modern"] [data-document-role="heroVisual"] {
                    min-height: 420px;
                }

                [data-web-template="craftsman-modern"] [data-document-role="heroVisual"] .site-builder-image {
                    min-height: 420px;
                }

                [data-web-template="craftsman-modern"] [data-web-region="footer"] .site-builder-grid {
                    gap: 2.25rem;
                }

                [data-web-template="craftsman-modern"] [data-web-region="proof"] > .site-builder-grid {
                    grid-template-columns: minmax(0, 1fr) !important;
                }

                [data-web-template="craftsman-modern"] [data-web-region="proof"] > .site-builder-grid > .site-builder-grid-item {
                    border-right: 0;
                    min-height: 112px;
                }

                [data-web-template="craftsman-modern"] [data-web-region$="Cta"] .site-builder-grid-item {
                    grid-column: 1 !important;
                    grid-row: auto !important;
                }

                [data-web-template="craftsman-modern"] [data-web-region$="Cta"] .site-builder-button {
                    margin-top: .5rem;
                    width: 100% !important;
                }
            }

            @media (prefers-reduced-motion: reduce) {
                [data-web-template="craftsman-modern"],
                [data-web-template="craftsman-modern"] * {
                    scroll-behavior: auto !important;
                }

                [data-web-template="craftsman-modern"] .site-builder-card,
                [data-web-template="craftsman-modern"] .site-builder-button,
                [data-web-template="craftsman-modern"] .site-builder-image,
                [data-web-template="craftsman-modern"] [data-craftsman-reveal] {
                    transition-duration: .01ms !important;
                }
            }
        `}</style>
    );
}

function CraftsmanFallbackPage({
    props,
    design,
    LinkComponent,
}: {
    props: TemplateRenderProps;
    design: ResolvedTemplateDesign;
    LinkComponent: TemplateLinkComponent;
}) {
    const page = props.page;

    if (!page) {
        return null;
    }

    return (
        <main
            className="px-5 py-20 sm:px-6"
            data-builder-document-region="page"
        >
            <article className="mx-auto max-w-[860px] leading-8 text-[var(--site-muted)] [&_a]:text-[var(--site-primary)] [&_blockquote]:border-l-2 [&_blockquote]:border-[var(--site-accent)] [&_blockquote]:pl-5 [&_h1]:mb-6 [&_h1]:font-[var(--site-heading-font)] [&_h1]:text-5xl [&_h1]:font-semibold [&_h1]:leading-tight [&_h1]:text-[var(--site-text)] [&_h2]:mb-3 [&_h2]:mt-9 [&_h2]:font-[var(--site-heading-font)] [&_h2]:text-3xl [&_h2]:font-semibold [&_h2]:text-[var(--site-text)] [&_li]:mb-2 [&_ol]:mb-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:mb-5 [&_strong]:text-[var(--site-text)] [&_ul]:mb-6 [&_ul]:list-disc [&_ul]:pl-6">
                <h1>{page.title}</h1>
                {renderTemplateContent(page, design, LinkComponent, props.leadForm, props.readOnly)}
            </article>
        </main>
    );
}

export const craftsmanModernTemplate: SiteTemplateDefinition = {
    manifest: {
        code: "craftsman-modern",
        name: "Řemeslník a dílna",
        type: "website",
        category: "trades",
        description: "Moderní mobilní šablona pro řemeslníky a menší dílny s důrazem na služby, realizace, důvěru a rychlou poptávku.",
        previewImage: "/templates/craftsman-modern/preview.jpg",
        defaults: webSystemTemplateDesignDefaults["craftsman-modern"],
        pageTypes: {
            home: {
                label: "Craftsman homepage",
                slots: ["richContent"],
            },
            content: {
                label: "Craftsman content page",
                slots: ["richContent"],
            },
        },
        editable: {
            colors: true,
            typography: true,
            radius: true,
            header: ["builder"],
            footer: ["builder"],
            buttons: ["solid", "outline", "pill"],
        },
    },
    render: (props: TemplateRenderProps) => {
        const design = mergeTemplateDesign(
            craftsmanModernTemplate.manifest.defaults,
            props.designOverride ?? props.site?.templateDesign,
        );
        const LinkComponent = props.LinkComponent ?? DefaultTemplateLink;

        return (
            <div
                data-web-template="craftsman-modern"
                data-web-template-editor={props.editorMode ? "true" : undefined}
                className="min-h-screen overflow-x-hidden bg-[var(--site-background)] font-[var(--site-body-font)] text-[var(--site-text)] antialiased"
                style={{
                    ...templateDesignToCssVars(design),
                    containerType: "inline-size",
                }}
            >
                <CraftsmanStyles />
                <WebGlobalBuilderRegion
                    design={design}
                    LinkComponent={LinkComponent}
                    props={props}
                    region="header"
                >
                    <CraftsmanMobileMenu
                        languageCode={props.page?.languageCode ?? props.site?.defaultLanguageCode}
                    />
                </WebGlobalBuilderRegion>
                <WebBuilderPageRegion
                    design={design}
                    fallback={(
                        <CraftsmanFallbackPage
                            props={props}
                            design={design}
                            LinkComponent={LinkComponent}
                        />
                    )}
                    LinkComponent={LinkComponent}
                    props={props}
                />
                <WebGlobalBuilderRegion
                    design={design}
                    LinkComponent={LinkComponent}
                    props={props}
                    region="footer"
                />
            </div>
        );
    },
};
